import React from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const { FiWifi, FiCoffee, FiMapPin } = FiIcons;
const { FaSwimmingPool, FaUmbrellaBeach, FaCar, FaSpa } = FaIcons;

const Home = () => {
  const highlights = [
    { icon: FaUmbrellaBeach, label: "Beachfront (60m)" },
    { icon: FaSwimmingPool, label: "Outdoor Pool" },
    { icon: FiWifi, label: "Free Wi-Fi" },
    { icon: FaCar, label: "Free Parking" },
    { icon: FiCoffee, label: "Restaurant & Bar" },
    { icon: FaSpa, label: "Spa & Wellness" },
  ];

  return (
    <div className="page-transition">
      {/* Hero Section with video background */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video 
            className="w-full h-full object-cover"
            autoPlay 
            loop 
            muted 
            playsInline
            src="https://canv.wasacon.com/amani-resort-zanzibar/_assets/video/b98a94b8488c6939bfb84926c651e336.mp4"
          />
          {/* Darker gradient overlay at the top to ensure menu readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/40"></div>
        </div>
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl lg:text-7xl font-serif mb-6 leading-tight drop-shadow-lg"
          >
            Your peaceful beachfront <br className="hidden md:block"/> escape in Zanzibar
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl font-light max-w-2xl mx-auto mb-10 text-brand-50 drop-shadow-md"
          >
            Amani Palms by Manolo Beach Resort welcomes you in Uroa, a calm garden retreat with warm island hospitality.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button to="/contact" variant="primary" className="bg-brand-500 border-none hover:bg-brand-400 px-10 shadow-lg">Book Your Stay</Button>
            <Button to="/rooms" variant="outline" className="text-white border-white hover:bg-white hover:text-brand-900 px-10 backdrop-blur-sm">Explore the Resort</Button>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-brand-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl mb-8 border-l-4 border-brand-300 pl-6">Welcome to Paradise</h2>
              <p className="text-brand-800 leading-relaxed mb-8 text-lg">
                Amani Palms by Manolo Beach Resort, located in Uroa, Zanzibar, offers accommodation with an outdoor swimming pool, free private parking, a garden and a terrace. Rooms are air-conditioned with garden views, include a desk, and offer free Wi-Fi.
              </p>
              <div className="bg-white p-8 rounded-xl shadow-sm border border-brand-100">
                <h3 className="text-xl font-serif mb-6 text-brand-900">Quick Facts</h3>
                <ul className="space-y-4 text-brand-600">
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FaUmbrellaBeach} className="text-brand-500 text-xl"/> 
                    <span>Distance to Uroa Public Beach: <b>60 m</b></span>
                  </li>
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FiMapPin} className="text-brand-500 text-xl"/> 
                    <span>Nearest airport: <b>Zanzibar Airport (34 km)</b></span>
                  </li>
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FaCar} className="text-brand-500 text-xl"/> 
                    <span>Paid airport shuttle available</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-1 md:order-2 grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1582719508461-905c673771fd?q=80&w=1000&auto=format&fit=crop" alt="Resort Detail" className="rounded-2xl object-cover h-80 w-full shadow-lg" />
              <img src="https://images.unsplash.com/photo-1445019980597-93fa8acb246c?q=80&w=1000&auto=format&fit=crop" alt="Beach View" className="rounded-2xl object-cover h-80 w-full shadow-lg translate-y-12" />
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl mb-4">Resort Highlights</h2>
            <div className="w-20 h-1 bg-brand-300 mx-auto mb-6"></div>
            <p className="text-brand-600 max-w-xl mx-auto">Everything you need for a serene and comfortable tropical getaway.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {highlights.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center p-8 bg-brand-50 rounded-2xl hover:bg-brand-100 hover:-translate-y-1 transition-all duration-300 group shadow-sm">
                <SafeIcon icon={item.icon} className="text-4xl text-brand-500 mb-5 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold tracking-widest uppercase text-center text-brand-900">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teaser Sections */}
      <section className="py-24 bg-brand-100">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-10">
            {/* Rooms */}
            <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500">
              <div className="overflow-hidden h-56">
                <img src="https://images.unsplash.com/photo-1611892440504-42a792e24d32?q=80&w=800&auto=format&fit=crop" alt="Rooms" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-serif mb-4">Comfortable Rooms</h3>
                <p className="text-sm text-brand-600 mb-6 leading-relaxed">Air-conditioned rooms with garden views, private bathrooms, and island-style decor.</p>
                <Button to="/rooms" variant="outline" className="w-full text-xs py-2">View Rooms</Button>
              </div>
            </div>

            {/* Dining */}
            <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500">
              <div className="overflow-hidden h-56">
                <img src="https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=800&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-serif mb-4">Exquisite Dining</h3>
                <p className="text-sm text-brand-600 mb-6 leading-relaxed">Savor African, Chinese, and Mediterranean flavors with fresh local ingredients.</p>
                <Button to="/dining" variant="outline" className="w-full text-xs py-2">Discover Dining</Button>
              </div>
            </div>

            {/* Wellness */}
            <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500">
              <div className="overflow-hidden h-56">
                <img src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=800&auto=format&fit=crop" alt="Wellness" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-serif mb-4">Spa & Wellness</h3>
                <p className="text-sm text-brand-600 mb-6 leading-relaxed">Restore your balance in our sanctuary of calm with professional wellness treatments.</p>
                <Button to="/wellness" variant="outline" className="w-full text-xs py-2">Relax Now</Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;